import axios from 'axios';
import { computed, toValue } from 'vue';
import { useQuery } from '@tanstack/vue-query';

import { queryKeys } from '@/queries/_shared/queryKeys';
import { getSessionServerId, getSessionToken } from '@/services/session';

const SOURCE_ID = Number(import.meta.env.VITE_SOURCE_ID || 0);
const PROFIWAY_BASE_URL = String(import.meta.env.VITE_PROFIWAY_BASE_URL || '').replace(/\/+$/, '');

const careerGuideClient = axios.create({
  baseURL: PROFIWAY_BASE_URL
});

function getLangCode() {
  return localStorage.getItem('translation') === 'kz' ? 'kk-KZ' : 'ru-RU';
}

careerGuideClient.interceptors.request.use((config) => {
  const token = getSessionToken();
  const institutionId = getSessionServerId();
  const lang = getLangCode();

  const nextConfig = { ...config };
  nextConfig.headers = { ...(nextConfig.headers || {}) };
  nextConfig.params = {
    ...(nextConfig.params || {}),
    source_id: SOURCE_ID,
    'x-lang': lang
  };

  if (token) {
    nextConfig.headers.Authorization = `Bearer ${token}`;
  }
  if (institutionId) {
    nextConfig.headers['x-server-id'] = institutionId;
  }
  nextConfig.headers['x-lang'] = lang;

  return nextConfig;
});

function unwrapBody(data) {
  if (data && typeof data === 'object' && 'body' in data) {
    return data.body;
  }
  return data;
}

async function fetchCareerPerformance(uin) {
  const response = await careerGuideClient.get('/pupil-portrait/performance', { params: { uin } });
  return unwrapBody(response.data);
}

async function fetchCareerSections(uin) {
  const response = await careerGuideClient.get('/pupil-portrait/sections', { params: { uin } });
  return unwrapBody(response.data);
}

async function fetchCareerEdaryn(uin) {
  const response = await careerGuideClient.get('/pupil-portrait/edaryn', { params: { uin } });
  return unwrapBody(response.data);
}

async function fetchCareerRecommendations(uin) {
  const response = await careerGuideClient.get('/recommendation/index', { params: { uin } });
  return unwrapBody(response.data);
}

export function useCareerGuidePerformanceQuery(uin, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.careerGuide.performance(toValue(uin))),
    queryFn: () => fetchCareerPerformance(toValue(uin)),
    enabled: computed(() => Boolean(toValue(uin))),
    ...options
  });
}

export function useCareerGuideSectionsQuery(uin, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.careerGuide.sections(toValue(uin))),
    queryFn: () => fetchCareerSections(toValue(uin)),
    enabled: computed(() => Boolean(toValue(uin))),
    ...options
  });
}

export function useCareerGuideEdarynQuery(uin, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.careerGuide.edaryn(toValue(uin))),
    queryFn: () => fetchCareerEdaryn(toValue(uin)),
    enabled: computed(() => Boolean(toValue(uin))),
    ...options
  });
}

export function useCareerGuideRecommendationsQuery(uin, options = {}) {
  return useQuery({
    queryKey: computed(() => queryKeys.careerGuide.recommendations(toValue(uin))),
    queryFn: () => fetchCareerRecommendations(toValue(uin)),
    enabled: computed(() => Boolean(toValue(uin))),
    retry: false,
    ...options
  });
}
